import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { hasActiveCreatorProfile } from '../lib/creatorPresentation';

/**
 * EarnWithLivesHero — Phase 45
 * ------------------------------------------------------------------
 * Shown at the top of the Live Dashboard EXCLUSIVELY to non-VIP users.
 * Positions the whole page as the entry point to monetization and
 * pushes the reader towards the Creator Onboarding Wizard.
 * The parent checks paid-feature eligibility; this presentation guard also
 * excludes already-active creators whose monetization is suspended.
 */
const EarnWithLivesHero = () => {
  const { t } = useTranslation('lives');
  const navigate = useNavigate();
  const { user } = useAuth();
  if (hasActiveCreatorProfile(user)) return null;

  return (
    <section
      className="mb-6 overflow-hidden rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-500/[0.08] via-amber-400/[0.03] to-transparent p-5 md:p-6"
      data-testid="live-dashboard-earn-hero"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-500/15 gold-text">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2
              className="text-lg font-semibold gold-text md:text-xl"
              data-testid="earn-hero-title"
            >
              {t('dashboard.earn_hero.title')}
            </h2>
            <p
              className="mt-1 text-[13px] leading-relaxed text-muted-foreground md:text-sm"
              data-testid="earn-hero-desc"
            >
              {t('dashboard.earn_hero.description')}
            </p>
          </div>
        </div>
        <Button
          onClick={() => navigate('/creator/onboarding')}
          className="gold-gradient shrink-0 text-sm font-semibold text-black"
          data-testid="earn-hero-cta"
        >
          {t('dashboard.earn_hero.cta')}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </section>
  );
};

export default EarnWithLivesHero;
